export interface NormalizedError {
  statusCode: number;
  title: string;
  message: string;
  fieldErrors: Map<string, string[]>;
  generalErrors: string[];
  errorType?:
    | "BUSINESS_RULE"
    | "VALIDATION"
    | "PERMISSION"
    | "SERVER"
    | "NETWORK";
  businessRuleType?: "CANNOT_REVERT_TWICE" | string;
  /** The backend's stable `code` field (e.g. "INSUFFICIENT_STOCK"), when present —
   *  lets the handler branch on a real contract instead of sniffing message text. */
  code?: string;
}

function getDefaultTitle(statusCode: number): string {
  if (statusCode === 0) return "Network Error";
  if (statusCode === 400) return "Validation Error";
  if (statusCode === 403) return "Permission Denied";
  if (statusCode >= 500) return "Server Error";
  return "Error";
}

function getDefaultMessage(statusCode: number): string {
  if (statusCode === 0) return "Cannot connect to server";
  if (statusCode === 400) return "Validation failed";
  if (statusCode === 403) return "Access forbidden";
  if (statusCode >= 500) return "Internal server error";
  return "An error occurred";
}

function detectBusinessRuleType(message: string): string | undefined {
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes("cannot revert twice in a row")) {
    return "CANNOT_REVERT_TWICE";
  }

  if (
    lowerMessage.includes("reached your plan limit") ||
    lowerMessage.includes("plan limit") ||
    lowerMessage.includes("subscription limit")
  ) {
    return "PLAN_LIMIT_EXCEEDED";
  }

  return undefined;
}

export function normalizeApiError(error: any): NormalizedError {
  const result: NormalizedError = {
    statusCode: 500,
    title: "Error",
    message: "An error occurred",
    fieldErrors: new Map(),
    generalErrors: [],
  };

  if (!error) return result;

  if (error.response?.status) {
    result.statusCode = error.response.status;
  } else if (!error.response) {
    result.statusCode = 0;
  }

  result.title =
    error.response?.data?.title || getDefaultTitle(result.statusCode);
  result.message =
    error.response?.data?.message || getDefaultMessage(result.statusCode);
  result.code = error.response?.data?.code;

  const businessRuleType = detectBusinessRuleType(result.message);
  if (businessRuleType) {
    result.errorType = "BUSINESS_RULE";
    result.businessRuleType = businessRuleType;
    result.title = "Business Rule Violation";
  }

  const errors = error.response?.data?.errors;
  if (Array.isArray(errors)) {
    errors.forEach((item) => {
      if (!item) return;

      const messages = Array.isArray(item.message)
        ? item.message
        : [item.message].filter(Boolean);

      messages.forEach((msg: string) => {
        const ruleType = detectBusinessRuleType(msg);
        if (ruleType && !result.businessRuleType) {
          result.errorType = "BUSINESS_RULE";
          result.businessRuleType = ruleType;
          result.title = "Business Rule Violation";
        }
      });

      if (!item.field || item.field === "general") {
        result.generalErrors.push(...messages);
      } else {
        result.fieldErrors.set(item.field, messages);
      }
    });
  }

  return result;
}
